(() => {
  if (customElements.get("mr-subscription-widget")) return;

  class MRSubscriptionWidget extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.dataElement = this.querySelector("[data-mr-subscription-data]");
      this.planInput = this.querySelector("[data-mr-selling-plan-input]");
      this.planSelect = this.querySelector("[data-mr-plan-select]");
      this.oneTimeChoice = this.querySelector('[data-mr-choice="one-time"]');
      this.subscriptionChoice = this.querySelector(
        '[data-mr-choice="subscription"]',
      );
      this.radios = Array.from(
        this.querySelectorAll(".mr-subscription-widget__radio"),
      );
      this.oneTimePrice = this.querySelector("[data-mr-one-time-price]");
      this.subscriptionPrice = this.querySelector(
        "[data-mr-subscription-price]",
      );
      this.status = this.querySelector("[data-mr-status]");
      this.requiresSellingPlan = this.dataset.requiresSellingPlan === "true";

      if (
        !this.form ||
        !this.dataElement ||
        !this.planInput ||
        !this.planSelect
      ) {
        this.hidden = true;
        return;
      }

      try {
        this.data = JSON.parse(this.dataElement.textContent);
      } catch (error) {
        console.error(
          "MaglieRandom subscription widget: invalid product data.",
          error,
        );
        this.hidden = true;
        return;
      }

      this.radios.forEach((radio) => {
        radio.addEventListener("change", () =>
          this.handlePurchaseOptionChange(radio),
        );
      });
      this.planSelect.addEventListener("change", () => this.applySelection());

      this.variantChangeHandler = (event) => {
        if (
          !event.detail ||
          String(event.detail.sectionId) !== String(this.dataset.sectionId)
        )
          return;
        this.updateVariant(event.detail.variant && event.detail.variant.id);
      };
      document.addEventListener(
        "product:variant-change",
        this.variantChangeHandler,
      );

      const variantInput = this.form.querySelector('[name="id"]');
      if (variantInput) {
        this.variantInput = variantInput;
        this.variantInputHandler = () => this.updateVariant(variantInput.value);
        variantInput.addEventListener("change", this.variantInputHandler);
      }

      this.updateVariant(variantInput && variantInput.value);
    }

    disconnectedCallback() {
      document.removeEventListener(
        "product:variant-change",
        this.variantChangeHandler,
      );
      if (this.variantInput && this.variantInputHandler) {
        this.variantInput.removeEventListener(
          "change",
          this.variantInputHandler,
        );
      }
    }

    handlePurchaseOptionChange(changedRadio) {
      this.radios.forEach((radio) => {
        radio.checked = radio === changedRadio;
      });
      this.applySelection();
    }

    updateVariant(variantId) {
      const variants = Array.isArray(this.data.variants)
        ? this.data.variants
        : [];
      this.variant =
        variants.find((variant) => String(variant.id) === String(variantId)) ||
        variants[0];

      if (!this.variant) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      const allocations = Array.isArray(this.variant.allocations)
        ? this.variant.allocations
        : [];
      const availablePlanIds = new Set(
        allocations.map((allocation) => String(allocation.planId)),
      );

      Array.from(this.planSelect.options).forEach((option) => {
        option.disabled = !availablePlanIds.has(String(option.value));
        option.hidden = option.disabled;
      });

      if (!availablePlanIds.has(String(this.planSelect.value))) {
        const firstAvailableOption = Array.from(this.planSelect.options).find(
          (option) => !option.disabled,
        );
        this.planSelect.value = firstAvailableOption
          ? firstAvailableOption.value
          : "";
      }

      const hasSubscription =
        allocations.length > 0 && Boolean(this.planSelect.value);
      this.subscriptionChoice.classList.toggle(
        "is-unavailable",
        !hasSubscription,
      );
      this.planSelect.disabled = !hasSubscription;

      if (!hasSubscription) {
        if (this.requiresSellingPlan) {
          this.setPurchaseOption("subscription");
          this.status.textContent =
            "L’abbonamento non è disponibile per questa variante.";
          this.setAddButtonsDisabled(true);
        } else {
          this.setPurchaseOption("one-time");
          this.status.textContent = "";
          this.setAddButtonsDisabled(false);
        }
      } else {
        this.status.textContent = "";
        this.setAddButtonsDisabled(false);
        if (!this.getSelectedRadio()) {
          this.setPurchaseOption(this.dataset.defaultOption);
        }
      }

      this.applySelection();
    }

    setPurchaseOption(value) {
      const radio = this.radios.find((item) => item.value === value);
      if (!radio) return;
      this.radios.forEach((item) => {
        item.checked = item === radio;
      });
    }

    getSelectedRadio() {
      return this.radios.find((radio) => radio.checked);
    }

    getSelectedAllocation() {
      if (!this.variant || !Array.isArray(this.variant.allocations))
        return null;
      return (
        this.variant.allocations.find(
          (allocation) =>
            String(allocation.planId) === String(this.planSelect.value),
        ) || null
      );
    }

    applySelection() {
      const selectedRadio = this.getSelectedRadio();
      const isSubscription = Boolean(
        selectedRadio && selectedRadio.value === "subscription",
      );
      const allocation = this.getSelectedAllocation();

      if (this.requiresSellingPlan && allocation) {
        this.setPurchaseOption("subscription");
      }

      const shouldUseSubscription =
        (this.requiresSellingPlan || isSubscription) && Boolean(allocation);

      this.classList.toggle("is-subscription-selected", shouldUseSubscription);
      if (this.oneTimeChoice) {
        this.oneTimeChoice.classList.toggle(
          "is-selected",
          !shouldUseSubscription,
        );
      }
      this.subscriptionChoice.classList.toggle(
        "is-selected",
        shouldUseSubscription,
      );

      const nextPlanValue = shouldUseSubscription
        ? String(allocation.planId)
        : "";
      const planChanged =
        this.planInput.disabled === shouldUseSubscription ||
        this.planInput.value !== nextPlanValue;

      this.planInput.disabled = !shouldUseSubscription;
      this.planInput.value = nextPlanValue;

      if (planChanged) {
        this.planInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      this.renderPrices(allocation);
    }

    renderPrices(allocation) {
      const oneTime = Number(this.variant && this.variant.price) || 0;
      if (this.oneTimePrice)
        this.oneTimePrice.textContent = this.formatMoney(oneTime);

      if (!allocation) {
        this.subscriptionPrice.textContent = "";
        return;
      }

      const subscription = Number(allocation.price) || 0;
      this.subscriptionPrice.textContent = this.formatMoney(subscription);
    }

    formatMoney(cents) {
      const locale = document.documentElement.lang || "it-IT";
      const currency = this.dataset.currency || "EUR";
      try {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(cents / 100);
      } catch (error) {
        return `${(cents / 100).toFixed(2).replace(".", ",")} ${currency}`;
      }
    }

    setAddButtonsDisabled(disabled) {
      const productArea = this.closest(".thb-product-detail") || document;
      productArea
        .querySelectorAll(".single-add-to-cart-button")
        .forEach((button) => {
          if (disabled) {
            button.setAttribute("disabled", "disabled");
            button.setAttribute("aria-disabled", "true");
          } else if (this.variant && this.variant.available) {
            button.removeAttribute("disabled");
            button.removeAttribute("aria-disabled");
          }
        });
    }
  }

  customElements.define("mr-subscription-widget", MRSubscriptionWidget);
})();
